import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Plus, Trash2, Download, Upload, Copy, Check, Pencil, X } from 'lucide-react';
import { API } from '../../content/data';
import { ARTICLES } from '../../content/articles';
import { MediaPicker } from './design';

const api = (key) => axios.create({ headers: { 'X-Admin-Key': key } });
const inputCls = 'w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors focus:border-electric placeholder:text-navy/40';
const labelCls = 'overline !text-[10px] mb-1.5 block text-navy/50';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline', 'blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image'],
    ['clean'],
  ],
};

function useAdminFetch(key, onAuthError) {
  return useCallback(async (fn) => {
    try {
      return await fn(api(key));
    } catch (e) {
      if (e.response?.status === 401) onAuthError();
      throw e;
    }
  }, [key, onAuthError]);
}

function SectionHead({ title, desc, children }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h2 className="text-h3 font-semibold text-navy">{title}</h2>
        {desc && <p className="mt-1 text-sm text-navy/55">{desc}</p>}
      </div>
      {children}
    </div>
  );
}

/* ---------------- Overview ---------------- */
function MiniBarChart({ data, testId }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div data-testid={testId}>
      <div className="flex h-40 items-end gap-1.5">
        {data.map((d) => (
          <div key={d.date} className="group flex flex-1 flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
            <span className="mb-1 font-mono text-[10px] font-semibold text-[#7367F0] opacity-0 transition-opacity group-hover:opacity-100">{d.count}</span>
            <div
              className="w-full rounded-t-md bg-[#7367F0]/85 transition-all duration-500 group-hover:bg-[#7367F0]"
              style={{ height: `${Math.max(4, (d.count / max) * 100)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between font-mono text-[10px] text-[#A5A3AE]">
        <span>{data[0]?.date.slice(5)}</span><span>{data[data.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}

function InterestBars({ data, testId }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  if (!data.length) return <p className="py-6 text-center text-sm text-[#A5A3AE]">No data yet — chart fills in as enquiries arrive.</p>;
  return (
    <div data-testid={testId} className="space-y-3">
      {data.map((d) => (
        <div key={d.label}>
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-[#4B465C]">{d.label}</span>
            <span className="font-mono text-[#A5A3AE]">{d.count}</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-[#F1F0F4]">
            <div className="h-full rounded-full bg-gradient-to-r from-[#7367F0] to-[#9E95F5] transition-all duration-700" style={{ width: `${(d.count / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function Overview({ adminKey, onAuthError }) {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const run = useAdminFetch(adminKey, onAuthError);
  useEffect(() => {
    run((c) => c.get(`${API}/admin/stats`)).then((r) => setStats(r.data)).catch(() => {});
    run((c) => c.get(`${API}/admin/analytics`)).then((r) => setAnalytics(r.data)).catch(() => {});
  }, [run]);
  const cards = [
    { label: 'Leads', value: stats?.leads }, { label: 'Enquiries', value: stats?.inquiries },
    { label: 'Articles', value: stats?.articles }, { label: 'Media Files', value: stats?.media },
    { label: 'Custom Links', value: stats?.links }, { label: 'Text Overrides', value: stats?.overrides },
  ];
  return (
    <div data-testid="admin-overview">
      <SectionHead title="Website Overview" desc="Everything on the site, at a glance.">
        <div className="flex gap-2">
          <button data-testid="weekly-pdf-button" onClick={() => window.open(`${API}/admin/reports/weekly.pdf?key=${encodeURIComponent(adminKey)}`, '_blank')} className="flex items-center gap-2 rounded-full bg-electric px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-navy">
            <Download className="h-3.5 w-3.5" /> Weekly PDF
          </button>
          <button data-testid="weekly-csv-button" onClick={() => window.open(`${API}/admin/reports/weekly.csv?key=${encodeURIComponent(adminKey)}`, '_blank')} className="flex items-center gap-2 rounded-full border border-navy/15 px-5 py-2.5 text-xs font-semibold text-navy transition-colors hover:border-electric hover:text-electric">
            <Download className="h-3.5 w-3.5" /> Weekly CSV
          </button>
        </div>
      </SectionHead>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        {cards.map((c) => (
          <div key={c.label} className="rounded-3xl border border-navy/10 bg-white p-5">
            <span className="overline !text-[10px] text-electric">{c.label}</span>
            <p className="mt-2 font-mono text-3xl font-semibold text-navy">{c.value ?? '—'}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-4 xl:grid-cols-2">
        <div className="rounded-3xl border border-navy/10 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-navy">Leads Over Time</h3>
              <p className="text-xs text-navy/50">Last 14 days · hover bars for counts</p>
            </div>
            <span className="rounded-full bg-electric/10 px-3 py-1 font-mono text-xs font-semibold text-electric">{stats?.leads ?? 0} total</span>
          </div>
          {analytics ? <MiniBarChart data={analytics.leadsByDay} testId="leads-chart" /> : <p className="py-10 text-center text-sm text-navy/40">Loading…</p>}
        </div>
        <div className="rounded-3xl border border-navy/10 bg-white p-6">
          <h3 className="text-base font-semibold text-navy">Top Enquiry Interests</h3>
          <p className="mb-4 text-xs text-navy/50">What leads and contacts are asking about</p>
          {analytics ? (
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <span className="overline !text-[10px] mb-3 block text-navy/45">Leads</span>
                <InterestBars data={analytics.leadsByInterest} testId="leads-interests" />
              </div>
              <div>
                <span className="overline !text-[10px] mb-3 block text-navy/45">Contact Enquiries</span>
                <InterestBars data={analytics.inquiriesByInterest} testId="inquiries-interests" />
              </div>
            </div>
          ) : <p className="py-10 text-center text-sm text-navy/40">Loading…</p>}
        </div>
      </div>

      <div className="mt-6 rounded-3xl bg-navy p-6 text-white sm:p-8">
        <h3 className="text-lg font-semibold">What you can manage here</h3>
        <ul className="mt-4 grid gap-2 text-sm text-white/70 sm:grid-cols-2">
          {['Download leads & enquiries as CSV', 'Create, edit and publish press articles & blogs', 'Upload images and documents', 'Change text on every page', 'Add buttons & links to header and footer', 'Update header CTA, footer statement & contact details', 'Set the scholarship countdown date', 'Publish alumni success stories'].map((t) => (
            <li key={t} className="flex items-start gap-2.5"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />{t}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------------- Leads ---------------- */
export function LeadsSection({ adminKey, onAuthError }) {
  const [tab, setTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const run = useAdminFetch(adminKey, onAuthError);
  useEffect(() => {
    run((c) => Promise.all([c.get(`${API}/admin/leads`), c.get(`${API}/admin/inquiries`)]))
      .then(([l, i]) => { setLeads(l.data); setInquiries(i.data); }).catch(() => {});
  }, [run]);
  const rows = tab === 'leads' ? leads : inquiries;
  return (
    <div data-testid="admin-leads-section">
      <SectionHead title="Leads & Enquiries" desc="Every form submission, stored centrally.">
        <button data-testid="admin-export-button" onClick={() => window.open(`${API}/admin/${tab}/export.csv?key=${encodeURIComponent(adminKey)}`, '_blank')} className="flex items-center gap-2 rounded-full bg-electric px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-navy">
          <Download className="h-3.5 w-3.5" /> Download CSV
        </button>
      </SectionHead>
      <div className="inline-flex rounded-full border border-navy/15 bg-white p-1">
        {[['leads', `Leads (${leads.length})`], ['inquiries', `Enquiries (${inquiries.length})`]].map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`touch-target rounded-full px-5 py-2 text-xs font-semibold transition-colors ${tab === k ? 'bg-navy text-gold' : 'text-navy/60'}`}>{l}</button>
        ))}
      </div>
      <div className="mt-6 overflow-x-auto rounded-3xl border border-navy/10 bg-white">
        <table data-testid="admin-table" className="w-full min-w-[720px] text-left text-sm">
          <thead><tr className="border-b border-navy/10 font-mono text-[11px] uppercase tracking-widest text-navy/50">
            <th className="px-5 py-3.5">Date</th><th className="px-5 py-3.5">Name</th><th className="px-5 py-3.5">Email</th><th className="px-5 py-3.5">Phone</th><th className="px-5 py-3.5">Org</th><th className="px-5 py-3.5">Interest</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-navy/5 last:border-0 hover:bg-paper/60">
                <td className="px-5 py-3 font-mono text-xs text-navy/55">{new Date(r.created_at).toLocaleString('en-IN')}</td>
                <td className="px-5 py-3 font-semibold">{r.name}</td>
                <td className="px-5 py-3">{r.email}</td>
                <td className="px-5 py-3 font-mono text-xs">{r.phone}</td>
                <td className="px-5 py-3">{r.company || r.organisation || '—'}</td>
                <td className="px-5 py-3"><span className="rounded-full bg-electric/10 px-3 py-1 text-xs font-semibold text-electric">{r.interest}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-navy/50">No entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Articles (rich text) ---------------- */
const EMPTY_ARTICLE = { title: '', slug: '', category: 'Perspectives', date: '', readTime: '5 min read', summary: '', question: '', context: '', perspective: '', example: '', meaning: '', cover: '', published: true };
const ARTICLE_FIELDS = [
  ['question', 'The Question'], ['context', 'The Context'],
  ['perspective', 'The DITEC Perspective'], ['example', 'Example'], ['meaning', 'What This Could Mean'],
];

export function ArticlesSection({ adminKey, onAuthError }) {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const run = useAdminFetch(adminKey, onAuthError);
  const load = useCallback(() => run((c) => c.get(`${API}/admin/articles`)).then((r) => setList(r.data)).catch(() => {}), [run]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await run((c) => c.put(`${API}/admin/articles/${editing.id}`, editing));
      else await run((c) => c.post(`${API}/admin/articles`, editing));
      setEditing(null);
      load();
    } finally { setSaving(false); }
  };

  const importDefaults = async () => {
    await run((c) => c.post(`${API}/admin/articles/bulk`, { articles: ARTICLES }));
    load();
  };

  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const setRich = (k) => (v) => setEditing((f) => ({ ...f, [k]: v }));

  if (editing) {
    return (
      <div data-testid="article-editor">
        <SectionHead title={editing.id ? 'Edit Article' : 'New Article'}>
          <button onClick={() => setEditing(null)} className="flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy"><X className="h-3.5 w-3.5" /> Cancel</button>
        </SectionHead>
        <div className="grid gap-4 rounded-3xl border border-navy/10 bg-white p-6 sm:grid-cols-2">
          <div><span className={labelCls}>Title</span><input data-testid="article-title-input" className={inputCls} value={editing.title} onChange={set('title')} /></div>
          <div><span className={labelCls}>Slug (optional)</span><input className={inputCls} value={editing.slug || ''} onChange={set('slug')} placeholder="auto-generated from title" /></div>
          <div><span className={labelCls}>Category</span>
            <select className={inputCls} value={editing.category} onChange={set('category')}>
              {['DITEC Stories', 'Student & Alumni', 'Enterprise', 'Research & Intelligence', 'Digital Economy', 'Perspectives'].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><span className={labelCls}>Date</span><input className={inputCls} value={editing.date} onChange={set('date')} placeholder="July 2026" /></div>
            <div><span className={labelCls}>Read Time</span><input className={inputCls} value={editing.readTime} onChange={set('readTime')} /></div>
          </div>
          <div className="sm:col-span-2"><span className={labelCls}>Cover Image URL (from Media Library)</span><input className={inputCls} value={editing.cover || ''} onChange={set('cover')} placeholder="/api/uploads/… or https://…" /></div>
          <div className="sm:col-span-2"><span className={labelCls}>Summary</span><textarea rows={2} className={`${inputCls} resize-y`} value={editing.summary} onChange={set('summary')} /></div>
          {ARTICLE_FIELDS.map(([k, l]) => (
            <div key={k} className="sm:col-span-2" data-testid={`article-rich-${k}`}>
              <span className={labelCls}>{l}</span>
              <div className="rte-wrap overflow-hidden rounded-xl border border-navy/15 bg-white">
                <ReactQuill theme="snow" value={editing[k] || ''} onChange={setRich(k)} modules={QUILL_MODULES} />
              </div>
            </div>
          ))}
          <label className="flex items-center gap-3 text-sm font-semibold text-navy sm:col-span-2">
            <input data-testid="article-published-toggle" type="checkbox" checked={!!editing.published} onChange={set('published')} className="h-5 w-5 accent-electric" /> Published (visible on the website)
          </label>
          <button data-testid="article-save-button" onClick={save} disabled={saving || !editing.title} className="touch-target rounded-full bg-electric px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-navy disabled:opacity-50 sm:col-span-2 sm:w-fit">
            {saving ? 'Saving…' : 'Save Article'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-articles-section">
      <SectionHead title="Press & Blogs" desc="Create, edit, publish or remove articles with rich formatting.">
        <button data-testid="article-new-button" onClick={() => setEditing({ ...EMPTY_ARTICLE })} className="flex items-center gap-2 rounded-full bg-electric px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-navy">
          <Plus className="h-3.5 w-3.5" /> New Article
        </button>
      </SectionHead>
      {list && list.length === 0 && (
        <div className="rounded-3xl border border-navy/10 bg-white p-8 text-center">
          <p className="text-sm text-navy/60">The site currently shows the built-in launch articles. Import them here to edit, or start fresh.</p>
          <button data-testid="articles-import-button" onClick={importDefaults} className="mt-4 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-gold transition-colors hover:bg-electric hover:text-white">
            Import 12 Launch Articles
          </button>
        </div>
      )}
      <div className="space-y-3">
        {(list || []).map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-navy/10 bg-white p-5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-navy">{a.title}</p>
              <p className="mt-1 font-mono text-[11px] text-navy/45">{a.category} · {a.published !== false ? 'Published' : 'Draft'} · /press/{a.slug}</p>
            </div>
            <button data-testid={`article-edit-${a.slug}`} onClick={() => setEditing(a)} className="flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy transition-colors hover:border-electric hover:text-electric"><Pencil className="h-3.5 w-3.5" /> Edit</button>
            <button data-testid={`article-delete-${a.slug}`} onClick={async () => { if (window.confirm('Delete this article?')) { await run((c) => c.delete(`${API}/admin/articles/${a.id}`)); load(); } }} className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Alumni Stories ---------------- */
const EMPTY_STORY = { name: '', role: '', quote: '', image: '', published: true };

export function StoriesSection({ adminKey, onAuthError }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const run = useAdminFetch(adminKey, onAuthError);
  const load = useCallback(() => run((c) => c.get(`${API}/admin/stories`)).then((r) => setList(r.data)).catch(() => {}), [run]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await run((c) => c.put(`${API}/admin/stories/${editing.id}`, editing));
      else await run((c) => c.post(`${API}/admin/stories`, editing));
      setEditing(null);
      load();
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <div data-testid="admin-stories-section">
      <SectionHead title="Alumni Stories" desc="Success stories shown in the Press & Insights carousel.">
        <button data-testid="story-new-button" onClick={() => setEditing({ ...EMPTY_STORY })} className="flex items-center gap-2 rounded-full bg-electric px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-navy">
          <Plus className="h-3.5 w-3.5" /> New Story
        </button>
      </SectionHead>

      {editing && (
        <div className="mb-6 grid gap-4 rounded-3xl border border-navy/10 bg-white p-6 sm:grid-cols-2" data-testid="story-editor">
          <div><span className={labelCls}>Name</span><input data-testid="story-name-input" className={inputCls} value={editing.name} onChange={set('name')} /></div>
          <div><span className={labelCls}>Role / Pathway</span><input className={inputCls} value={editing.role} onChange={set('role')} placeholder="Flagship Learner · Digital Marketer" /></div>
          <div className="sm:col-span-2"><span className={labelCls}>Quote</span><textarea rows={3} className={`${inputCls} resize-y`} value={editing.quote} onChange={set('quote')} /></div>
          <div className="sm:col-span-2"><span className={labelCls}>Photo URL (from Media Library, optional)</span><input className={inputCls} value={editing.image || ''} onChange={set('image')} placeholder="/api/uploads/… or https://…" /></div>
          <label className="flex items-center gap-3 text-sm font-semibold text-navy">
            <input data-testid="story-published-toggle" type="checkbox" checked={!!editing.published} onChange={set('published')} className="h-5 w-5 accent-electric" /> Published
          </label>
          <div className="flex gap-2 sm:justify-end">
            <button onClick={() => setEditing(null)} className="rounded-full border border-navy/15 px-5 py-2.5 text-xs font-semibold text-navy">Cancel</button>
            <button data-testid="story-save-button" onClick={save} disabled={saving || !editing.name} className="rounded-full bg-electric px-6 py-2.5 text-xs font-semibold text-white hover:bg-navy disabled:opacity-50">{saving ? 'Saving…' : 'Save Story'}</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {list.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-navy/10 bg-white p-5">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-navy">{s.name} <span className="font-normal text-navy/50">— {s.role}</span></p>
              <p className="mt-1 truncate font-serif text-xs italic text-navy/55">“{s.quote}”</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${s.published !== false ? 'bg-electric/10 text-electric' : 'bg-navy/10 text-navy/50'}`}>{s.published !== false ? 'Published' : 'Draft'}</span>
            <button data-testid={`story-edit-${s.id.slice(0, 8)}`} onClick={() => setEditing(s)} className="flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy hover:border-electric hover:text-electric"><Pencil className="h-3.5 w-3.5" /> Edit</button>
            <button data-testid={`story-delete-${s.id.slice(0, 8)}`} onClick={async () => { if (window.confirm('Delete this story?')) { await run((c) => c.delete(`${API}/admin/stories/${s.id}`)); load(); } }} className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {list.length === 0 && !editing && <p className="rounded-3xl border border-navy/10 bg-white p-10 text-center text-sm text-navy/50">No stories yet — the site shows illustrative samples until you publish real ones.</p>}
      </div>
    </div>
  );
}

/* ---------------- Media ---------------- */
export function MediaSection({ adminKey, onAuthError }) {
  const [list, setList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);
  const run = useAdminFetch(adminKey, onAuthError);
  const load = useCallback(() => run((c) => c.get(`${API}/admin/media`)).then((r) => setList(r.data)).catch(() => {}), [run]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try { await run((c) => c.post(`${API}/admin/media`, fd)); load(); } finally { setUploading(false); e.target.value = ''; }
  };

  const fullUrl = (m) => `${window.location.origin}${m.url}`;
  const copy = (m) => { navigator.clipboard.writeText(fullUrl(m)); setCopied(m.id); setTimeout(() => setCopied(null), 1500); };

  return (
    <div data-testid="admin-media-section">
      <SectionHead title="Media Library" desc="Upload images and documents — use their URLs anywhere on the site.">
        <label data-testid="media-upload-button" className="flex cursor-pointer items-center gap-2 rounded-full bg-electric px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-navy">
          <Upload className="h-3.5 w-3.5" /> {uploading ? 'Uploading…' : 'Upload File'}
          <input type="file" className="hidden" onChange={upload} accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ttf,.otf,.woff,.woff2" />
        </label>
      </SectionHead>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {list.map((m) => (
          <div key={m.id} data-testid={`media-item-${m.id.slice(0, 8)}`} className="overflow-hidden rounded-2xl border border-navy/10 bg-white">
            <div className="flex h-32 items-center justify-center bg-paper">
              {m.kind === 'image' ? <img src={m.url} alt={m.original} className="h-full w-full object-cover" /> : <span className="font-mono text-xs uppercase text-navy/40">{m.original?.split('.').pop() || 'doc'}</span>}
            </div>
            <div className="p-3">
              <p className="truncate text-xs font-semibold text-navy">{m.original}</p>
              <p className="font-mono text-[10px] text-navy/40">{(m.size / 1024).toFixed(0)} KB</p>
              <div className="mt-2 flex gap-1.5">
                <button onClick={() => copy(m)} className="flex flex-1 items-center justify-center gap-1 rounded-full border border-navy/15 py-1.5 text-[11px] font-semibold text-navy hover:border-electric hover:text-electric">
                  {copied === m.id ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} URL
                </button>
                <button onClick={async () => { if (window.confirm('Delete this file?')) { await run((c) => c.delete(`${API}/admin/media/${m.id}`)); load(); } }} className="rounded-full border border-red-200 px-3 py-1.5 text-red-600 hover:bg-red-50"><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {list.length === 0 && <p className="rounded-3xl border border-navy/10 bg-white p-10 text-center text-sm text-navy/50">No files uploaded yet.</p>}
    </div>
  );
}

/* ---------------- Page Texts ---------------- */
const TEXT_GROUPS = [
  { page: 'Home', keys: [
    ['home.hero.badge', 'Hero badge text'],
    ['home.hero.line1', 'Hero headline line 1'],
    ['home.hero.line2', 'Hero headline line 2'],
    ['home.hero.line3', 'Hero headline line 3'],
    ['home.hero.image', 'Hero image URL — replaces the headline with a responsive image (from Media Library)'],
    ['home.hero.intro', 'Hero paragraph', true],
    ['home.premise.heading', '"Where Capability Meets Opportunity" heading', true],
    ['home.scholarship.heading', 'Scholarship band heading', true],
    ['home.vision.text', 'Bigger Vision paragraph', true],
  ]},
  { page: 'About', keys: [['about.tag', 'Hero tag'], ['about.title', 'Hero title', true], ['about.intro', 'Hero intro', true], ['about.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Students', keys: [['students.tag', 'Hero tag'], ['students.title', 'Hero title', true], ['students.intro', 'Hero intro', true], ['students.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Programmes', keys: [['programmes.tag', 'Hero tag'], ['programmes.title', 'Hero title', true], ['programmes.intro', 'Hero intro', true], ['programmes.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Flagship (18-Month)', keys: [['flagship.tag', 'Hero tag'], ['flagship.title', 'Hero title', true], ['flagship.intro', 'Hero intro', true], ['flagship.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Technical Track', keys: [['technical.tag', 'Hero tag'], ['technical.title', 'Hero title', true], ['technical.intro', 'Hero intro', true], ['technical.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Business & Creator', keys: [['business.tag', 'Hero tag'], ['business.title', 'Hero title', true], ['business.intro', 'Hero intro', true], ['business.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Scholarship', keys: [['scholarship.title', 'Hero title', true], ['scholarship.intro', 'Hero intro', true]] },
  { page: 'Enterprises', keys: [['enterprises.tag', 'Hero tag'], ['enterprises.title', 'Hero title', true], ['enterprises.intro', 'Hero intro', true], ['enterprises.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'CSR Routing', keys: [['csr.tag', 'Hero tag'], ['csr.title', 'Hero title', true], ['csr.intro', 'Hero intro', true], ['csr.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Talent', keys: [['talent.tag', 'Hero tag'], ['talent.title', 'Hero title', true], ['talent.intro', 'Hero intro', true], ['talent.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Marketing Cell', keys: [['marketing.tag', 'Hero tag'], ['marketing.title', 'Hero title', true], ['marketing.intro', 'Hero intro', true], ['marketing.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Research', keys: [['research.tag', 'Hero tag'], ['research.title', 'Hero title', true], ['research.intro', 'Hero intro', true], ['research.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Membership', keys: [['membership.tag', 'Hero tag'], ['membership.title', 'Hero title', true], ['membership.intro', 'Hero intro', true], ['membership.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Institutions', keys: [['institutions.tag', 'Hero tag'], ['institutions.title', 'Hero title', true], ['institutions.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Press & Insights', keys: [['press.tag', 'Hero tag'], ['press.title', 'Hero title', true], ['press.intro', 'Hero intro', true], ['press.image', 'Hero image URL — replaces the hero with a responsive image']] },
  { page: 'Contact', keys: [['contact.tag', 'Hero tag'], ['contact.title', 'Hero title', true], ['contact.intro', 'Hero intro', true], ['contact.image', 'Hero image URL — replaces the hero with a responsive image']] },
];

export function TextsSection({ adminKey, onAuthError }) {
  const [overrides, setOverrides] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savedKey, setSavedKey] = useState(null);
  const run = useAdminFetch(adminKey, onAuthError);
  useEffect(() => {
    run((c) => c.get(`${API}/admin/content`)).then((r) => {
      const map = {};
      r.data.forEach((d) => { map[d.key] = d.value; });
      setOverrides(map);
    }).catch(() => {});
  }, [run]);

  const save = async (key) => {
    const value = drafts[key];
    if (value === undefined) return;
    await run((c) => c.put(`${API}/admin/content/${encodeURIComponent(key)}`, { value }));
    setOverrides((o) => ({ ...o, [key]: value }));
    setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
    setSavedKey(key);
    setTimeout(() => setSavedKey(null), 1500);
  };

  const reset = async (key) => {
    await run((c) => c.delete(`${API}/admin/content/${encodeURIComponent(key)}`));
    setOverrides((o) => { const n = { ...o }; delete n[key]; return n; });
    setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
  };

  return (
    <div data-testid="admin-texts-section">
      <SectionHead title="Page Texts" desc="Override the main text on any page. Leave blank to keep the default wording." />
      <div className="space-y-6">
        {TEXT_GROUPS.map((g) => (
          <div key={g.page} className="rounded-3xl border border-navy/10 bg-white p-6">
            <h3 className="text-base font-semibold text-navy">{g.page}</h3>
            <div className="mt-4 space-y-4">
              {g.keys.map(([key, label, multi]) => {
                const current = drafts[key] ?? overrides[key] ?? '';
                const dirty = drafts[key] !== undefined && drafts[key] !== (overrides[key] ?? '');
                return (
                  <div key={key} data-testid={`text-field-${key}`}>
                    <div className="flex items-center justify-between">
                      <span className={labelCls}>{label} {overrides[key] !== undefined && <span className="ml-1 rounded-full bg-gold px-2 py-0.5 font-sans !text-[9px] normal-case tracking-normal text-navy">customised</span>}</span>
                      {overrides[key] !== undefined && <button onClick={() => reset(key)} className="text-[11px] font-semibold text-red-500 hover:underline">Reset to default</button>}
                    </div>
                    {multi ? (
                      <textarea rows={2} className={`${inputCls} resize-y`} value={current} placeholder="(default text)" onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))} />
                    ) : (
                      <input className={inputCls} value={current} placeholder="(default text)" onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))} />
                    )}
                    {key.endsWith('.image') && (
                      <MediaPicker adminKey={adminKey} kinds={['image', 'video']} onPick={(url) => setDrafts((d) => ({ ...d, [key]: url }))} testId={`browse-${key}`} />
                    )}
                    {dirty && (
                      <button data-testid={`text-save-${key}`} onClick={() => save(key)} className="mt-2 rounded-full bg-electric px-5 py-2 text-xs font-semibold text-white hover:bg-navy">
                        {savedKey === key ? 'Saved ✓' : 'Save'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Links ---------------- */
export function LinksSection({ adminKey, onAuthError }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ label: '', url: '', location: 'footer' });
  const run = useAdminFetch(adminKey, onAuthError);
  const load = useCallback(() => run((c) => c.get(`${API}/links`)).then((r) => setList(r.data || [])).catch(() => {}), [run]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    await run((c) => c.post(`${API}/admin/links`, form));
    setForm({ label: '', url: '', location: 'footer' });
    load();
  };

  return (
    <div data-testid="admin-links-section">
      <SectionHead title="Buttons & Links" desc="Add custom links to the header navigation or the footer." />
      <form onSubmit={add} className="grid gap-3 rounded-3xl border border-navy/10 bg-white p-6 sm:grid-cols-[1fr_1.4fr_160px_auto]">
        <input data-testid="link-label-input" required className={inputCls} placeholder="Label (e.g. Brochure)" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
        <input data-testid="link-url-input" required className={inputCls} placeholder="URL (https://… or /page)" value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} />
        <select data-testid="link-location-select" className={inputCls} value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}>
          <option value="header">Header menu</option>
          <option value="footer">Footer</option>
        </select>
        <button data-testid="link-add-button" className="flex touch-target items-center justify-center gap-2 rounded-full bg-electric px-5 py-3 text-xs font-semibold text-white hover:bg-navy"><Plus className="h-3.5 w-3.5" /> Add</button>
      </form>
      <div className="mt-6 space-y-3">
        {list.map((l) => (
          <div key={l.id} className="flex items-center gap-4 rounded-2xl border border-navy/10 bg-white p-4">
            <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${l.location === 'header' ? 'bg-electric/10 text-electric' : 'bg-gold/60 text-navy'}`}>{l.location}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-navy">{l.label}</p>
              <p className="truncate font-mono text-[11px] text-navy/45">{l.url}</p>
            </div>
            <button data-testid={`link-delete-${l.id.slice(0, 8)}`} onClick={async () => { await run((c) => c.delete(`${API}/admin/links/${l.id}`)); load(); }} className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {list.length === 0 && <p className="rounded-3xl border border-navy/10 bg-white p-10 text-center text-sm text-navy/50">No custom links yet.</p>}
      </div>
    </div>
  );
}

/* ---------------- Settings (Header & Footer + Scholarship) ---------------- */
export function SettingsSection({ adminKey, onAuthError }) {
  const [form, setForm] = useState({ navCtaLabel: '', footerStatement: '', contactEmail: '', contactPhone: '', scholarshipLabel: '', scholarshipDate: '' });
  const [saved, setSaved] = useState(false);
  const run = useAdminFetch(adminKey, onAuthError);
  useEffect(() => {
    run((c) => c.get(`${API}/settings`)).then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {});
  }, [run]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    await run((c) => c.put(`${API}/admin/settings`, form));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  const FIELDS = [
    ['navCtaLabel', 'Header button label', 'Apply / Enquire', 'text'],
    ['footerStatement', 'Footer statement', 'Building capability for a more connected digital economy.', 'text'],
    ['contactEmail', 'Contact email', 'ditec.org@gmail.com', 'text'],
    ['contactPhone', 'Contact phone', '75038 50355', 'text'],
    ['scholarshipLabel', 'Scholarship date label (shown next to the countdown)', '9 December 2026', 'text'],
    ['scholarshipDate', 'Scholarship countdown date & time', '2026-12-09T09:00', 'datetime-local'],
  ];
  return (
    <div data-testid="admin-settings-section">
      <SectionHead title="Header, Footer & Scholarship" desc="Update global site content and the scholarship countdown." />
      <div className="max-w-2xl space-y-4 rounded-3xl border border-navy/10 bg-white p-6">
        {FIELDS.map(([k, label, ph, type]) => (
          <div key={k}>
            <span className={labelCls}>{label}</span>
            <input data-testid={`setting-${k}`} type={type} className={inputCls} value={form[k] || ''} placeholder={ph} onChange={set(k)} />
          </div>
        ))}
        <button data-testid="settings-save-button" onClick={save} className="touch-target rounded-full bg-electric px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy">
          {saved ? 'Saved ✓' : 'Save Changes'}
        </button>
        <p className="text-xs text-navy/45">Refresh the public site after saving to see changes. Clearing the scholarship date restores the default (9 December 2026).</p>
      </div>
    </div>
  );
}
